import { useState } from 'react';
import { createPortal } from 'react-dom';

import { MeteredPaywallSubscribeModal, MeteredPaywallUsageBanner, PaywallElement } from '@shared/dream-components';

import useGatedSubscribe from '~/hooks/useGatedSubscribe';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { AnyPaywall } from '~/interfaces/paywall';
import { isMeteredPaywall, toPaywallStyleProps, toUsageStyleProps } from '~/interfaces/paywall';
import type { PostLoaderData } from '~/routes/p/$slug';

interface Props {
  paywall: AnyPaywall | null;
  showPaywallAndHidePostContent: boolean;
  showMeteredUsageBanner: boolean;
  paywallUrl?: string;
}

const PostContentPaywall = ({ paywall, showPaywallAndHidePostContent, showMeteredUsageBanner, paywallUrl }: Props) => {
  const { handleSubscribe, referralId } = useGatedSubscribe();
  const { postMetaInformation } = useTypedLoaderData<PostLoaderData>();
  // Only true after the client loader's fetchPostMetaInformation resolves with
  // personalized (user-session-aware) data. Prevents flashing the metered paywall
  // modal during hydration when the CDN-cached server response may carry stale values.
  const isPostMetaFetched = !!postMetaInformation?._fetched;
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  const meteredModalProps = (premiumOfferId?: string) => ({
    onSubscribe: handleSubscribe,
    upgradePath: '/upgrade',
    premiumOfferId,
    referralId,
    loginUrl: '/login',
  });

  if (showPaywallAndHidePostContent && paywall && isPostMetaFetched) {
    // Case 1: not logged in — PaywallsController returns metered paywall → show subscribe modal
    if (isMeteredPaywall(paywall)) {
      return (
        <MeteredPaywallSubscribeModal
          styles={toPaywallStyleProps(paywall.paywall_styles)}
          copy={{ ...paywall.paywall_copy, showTosConsent: paywall.show_tos_consent }}
          {...meteredModalProps(paywall.premium_offer_id)}
        />
      );
    }

    // Case 4: limit exceeded — PaywallsController returns standard paywall → show normal paywall
    return (
      <PaywallElement
        theme={paywall.theme}
        upgradeUrl={paywallUrl}
        shouldUseUrl
        upgradePath="/upgrade"
        isLive
        element={{
          type: 'paywall_break',
          attrs: {
            id: paywall.id || 'post-page-paywall',
            data: {
              title: paywall.title,
              description: paywall.description,
              cta_text: paywall.cta_text,
              benefits: paywall.benefits,
              subheader: paywall.subheader,
              image_url: paywall.image_url,
              style: paywall.style,
            },
          },
          content: [{ id: 'text', text: '' }],
        }}
      >
        {null}
      </PaywallElement>
    );
  }

  if (showMeteredUsageBanner && !isBannerDismissed && paywall && isMeteredPaywall(paywall)) {
    // Portal to document.body so the banner's z-index operates in the root stacking context,
    // preventing any ancestor transform/stacking context from hiding it behind the footer.
    return createPortal(
      <MeteredPaywallUsageBanner
        {...toUsageStyleProps(paywall.usage_styles)}
        {...paywall.usage_copy}
        viewedCount={postMetaInformation?.metered_viewed_count ?? undefined}
        upgradePath="/upgrade"
        premiumOfferId={paywall.premium_offer_id}
        onClose={() => setIsBannerDismissed(true)}
      />,
      document.body,
    );
  }

  return null;
};

export { PostContentPaywall };
